// Pages Atlas dédiées — variantes de contenu
// Template unique paramétré via le slug, toutes présentées sous la marque geoVINUM Atlas.
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, Smartphone, Compass, Building2, Check, Sparkles,
  Users, Globe, GraduationCap, Briefcase, Award,
} from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import DownloadCatalogButton from "@/components/DownloadCatalogButton";
import { useT } from "@/lib/i18n";

const sub = (s, vars) => Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

const APP_URL = "https://app.geovinum.io";

// Static config — strings are i18n keys resolved at render time.
const ATLAS_CONFIG = {
  mini: {
    name: "geoVINUM Atlas",
    taglineKey: "atlasP.mini.tagline",
    Icon: Smartphone,
    heroImage: "/images/cartes-interactives.jpg",
    ctaPrimary: { labelKey: "atlasP.mini.cta_primary", url: APP_URL, external: true },
    ctaSecondary: { labelKey: "atlasP.mini.cta_secondary", url: "/atlas/magnum", external: false },
    priceKey: "atlasP.mini.price",
    priceNoteKey: "atlasP.mini.price_note",
    trial: null,
    audience: { Icon: Users, labelKey: "atlasP.mini.audience.label", descKey: "atlasP.mini.audience.desc" },
    whenKey: "atlasP.mini.when",
    whyKey: "atlasP.mini.why",
    featureKeys: ["atlasP.mini.feat.1", "atlasP.mini.feat.2", "atlasP.mini.feat.3", "atlasP.mini.feat.4", "atlasP.mini.feat.5"],
    upsellCards: [
      { slug: "magnum", titleKey: "atlasP.mini.upsell.magnum.title", descKey: "atlasP.mini.upsell.magnum.desc", to: "/atlas/magnum" },
      { slug: "academy", titleKey: "atlasP.mini.upsell.academy.title", descKey: "atlasP.mini.upsell.academy.desc", to: "/academy" },
    ],
  },
  magnum: {
    name: "geoVINUM Atlas",
    taglineKey: "atlasP.magnum.tagline",
    Icon: Compass,
    heroImage: "/images/magnum.png",
    ctaPrimary: { labelKey: "atlasP.magnum.cta_primary", url: APP_URL, external: true },
    ctaSecondary: { labelKey: "atlasP.magnum.cta_secondary", url: "/atlas/pro", external: false },
    priceKey: "atlasP.magnum.price",
    priceNoteKey: "atlasP.magnum.price_note",
    trial: "30",
    audience: { Icon: GraduationCap, labelKey: "atlasP.magnum.audience.label", descKey: "atlasP.magnum.audience.desc" },
    whenKey: "atlasP.magnum.when",
    whyKey: "atlasP.magnum.why",
    featureKeys: ["atlasP.magnum.feat.1", "atlasP.magnum.feat.2", "atlasP.magnum.feat.3", "atlasP.magnum.feat.4", "atlasP.magnum.feat.5", "atlasP.magnum.feat.6"],
    upsellCards: [
      { slug: "pro", titleKey: "atlasP.magnum.upsell.pro.title", descKey: "atlasP.magnum.upsell.pro.desc", to: "/atlas/pro" },
      { slug: "shop", titleKey: "atlasP.magnum.upsell.shop.title", descKey: "atlasP.magnum.upsell.shop.desc", to: "/shop" },
    ],
  },
  pro: {
    name: "geoVINUM Atlas",
    taglineKey: "atlasP.pro.tagline",
    Icon: Building2,
    heroImage: "/images/iframe.jpg",
    ctaPrimary: { labelKey: "atlasP.pro.cta_primary", url: "https://calendar.app.google/9ghfPJuKTf7fU6sS9", external: true },
    ctaSecondary: { labelKey: "atlasP.pro.cta_secondary", url: APP_URL, external: true },
    priceKey: "atlasP.pro.price",
    priceNoteKey: "atlasP.pro.price_note",
    trial: "30",
    audience: { Icon: Briefcase, labelKey: "atlasP.pro.audience.label", descKey: "atlasP.pro.audience.desc" },
    whenKey: "atlasP.pro.when",
    whyKey: "atlasP.pro.why",
    featureKeys: ["atlasP.pro.feat.1", "atlasP.pro.feat.2", "atlasP.pro.feat.3", "atlasP.pro.feat.4", "atlasP.pro.feat.5", "atlasP.pro.feat.6", "atlasP.pro.feat.7"],
    upsellCards: [
      { slug: "iframe", titleKey: "atlasP.pro.upsell.iframe.title", descKey: "atlasP.pro.upsell.iframe.desc", to: "/products/iframe" },
      { slug: "business", titleKey: "atlasP.pro.upsell.business.title", descKey: "atlasP.pro.upsell.business.desc", to: "/business" },
    ],
  },
};

const AtlasProductPage = () => {
  const t = useT();
  const { variant } = useParams();
  const cfg = ATLAS_CONFIG[variant];
  if (!cfg) {
    return (
      <Layout testId="atlas-not-found">
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <div className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{t("atlasP.not_found.title")}</div>
            <Link to="/explore" className="btn-primary">{t("atlasP.not_found.cta")} <ArrowRight size={14} /></Link>
          </div>
        </div>
      </Layout>
    );
  }
  const { Icon, audience } = cfg;
  const tagline = t(cfg.taglineKey);
  return (
    <Layout testId={`page-atlas-${variant}`}>
      <SEO title={`${cfg.name} | geoVINUM`} description={tagline} />

      {/* Hero */}
      <section className="pt-10 md:pt-16 pb-16 bg-[color:var(--gv-mist)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("atlasP.bc.home") }, { to: "/explore", label: t("atlasP.bc.atlas") }, { label: cfg.name }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[11px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-5" data-testid={`atlas-${variant}-eyebrow`}>
                <Icon size={13} /> {cfg.name}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] text-[color:var(--gv-ink)] mb-5" data-testid={`atlas-${variant}-title`}>
                {tagline}
              </h1>
              {cfg.trial && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] text-[12px] font-medium mb-5" data-testid={`atlas-${variant}-trial-badge`}>
                  <Sparkles size={12} /> {sub(t("atlasP.trial_days"), { days: cfg.trial })}
                </div>
              )}
              <div className="text-[14px] text-[color:var(--gv-ink)]/70 mb-7">
                <span className="font-display text-[20px] text-[color:var(--gv-ink)]">{t(cfg.priceKey)}</span>
                <span className="ml-2 text-[12.5px]">· {t(cfg.priceNoteKey)}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={cfg.ctaPrimary.url}
                  target={cfg.ctaPrimary.external ? "_blank" : undefined}
                  rel={cfg.ctaPrimary.external ? "noreferrer" : undefined}
                  className="btn-primary"
                  data-testid={`atlas-${variant}-cta-primary`}
                >
                  {t(cfg.ctaPrimary.labelKey)} <ArrowRight size={14} />
                </a>
                {cfg.ctaSecondary.external ? (
                  <a href={cfg.ctaSecondary.url} target="_blank" rel="noreferrer" className="btn-secondary" data-testid={`atlas-${variant}-cta-secondary`}>
                    {t(cfg.ctaSecondary.labelKey)}
                  </a>
                ) : (
                  <Link to={cfg.ctaSecondary.url} className="btn-secondary" data-testid={`atlas-${variant}-cta-secondary`}>
                    {t(cfg.ctaSecondary.labelKey)}
                  </Link>
                )}
                <DownloadCatalogButton variant="ghost" productSlug={variant} testId={`atlas-${variant}-pdf-download`} />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] rounded-[5px] overflow-hidden border border-[color:var(--gv-line)] bg-white shadow-[0_40px_80px_-40px_rgba(46,80,103,0.4)]">
                <img src={cfg.heroImage} alt={cfg.name} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO · WHEN · WHY */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card data-testid={`atlas-${variant}-who`} icon={<audience.Icon size={22} className="text-[color:var(--gv-pink)]" />} label={t("atlasP.who.label")} title={t(audience.labelKey)} desc={t(audience.descKey)} />
            <Card data-testid={`atlas-${variant}-when`} icon={<Globe size={22} className="text-[color:var(--gv-blue)]" />} label={t("atlasP.when.label")} title={t("atlasP.when.title")} desc={t(cfg.whenKey)} />
            <Card data-testid={`atlas-${variant}-why`} icon={<Award size={22} className="text-[color:var(--gv-ink)]" />} label={t("atlasP.why.label")} title={t("atlasP.why.title")} desc={t(cfg.whyKey)} />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-[color:var(--gv-mist)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-3">{t("atlasP.features.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]" data-testid={`atlas-${variant}-features-title`}>
              {sub(t("atlasP.features.title"), { name: cfg.name })}
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 max-w-5xl mx-auto" data-testid={`atlas-${variant}-features`}>
            {cfg.featureKeys.map((fk) => (
              <li key={fk} className="flex items-start gap-3 text-[14px] text-[color:var(--gv-ink)]/85">
                <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[color:var(--gv-pink)] text-white shrink-0">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span>{t(fk)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Cross-sell */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">{t("atlasP.upsell.title")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cfg.upsellCards.map((u) => (
              <Link
                key={u.slug}
                to={u.to}
                className="group block bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-6 hover:border-[color:var(--gv-pink)] transition-colors"
                data-testid={`atlas-${variant}-upsell-${u.slug}`}
              >
                <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2 group-hover:text-[color:var(--gv-pink)] transition-colors">{t(u.titleKey)}</div>
                <div className="text-[14px] text-[color:var(--gv-ink)]/70 mb-4">{t(u.descKey)}</div>
                <div className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-pink)]">
                  {t("atlasP.upsell.discover")} <ArrowRight size={13} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-[color:var(--gv-pink)] text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl mb-4" data-testid={`atlas-${variant}-final-title`}>
            {sub(t("atlasP.final.title"), { name: cfg.name })}
          </h2>
          {cfg.trial && (
            <p className="text-[14px] text-white/90 mb-6">
              {sub(t("atlasP.final.trial"), { days: cfg.trial })}
            </p>
          )}
          <a
            href={cfg.ctaPrimary.url}
            target={cfg.ctaPrimary.external ? "_blank" : undefined}
            rel={cfg.ctaPrimary.external ? "noreferrer" : undefined}
            className="inline-flex items-center gap-2 bg-white text-[color:var(--gv-pink)] px-6 py-3 rounded-[5px] font-medium hover:bg-white/90 transition-colors"
            data-testid={`atlas-${variant}-final-cta`}
          >
            {t(cfg.ctaPrimary.labelKey)} <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </Layout>
  );
};

const Card = ({ icon, label, title, desc, ...rest }) => (
  <div className="bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-6 md:p-7" {...rest}>
    <div className="mb-4">{icon}</div>
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5">{label}</div>
    <div className="font-display text-[18px] text-[color:var(--gv-ink)] mb-2">{title}</div>
    <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{desc}</p>
  </div>
);

export default AtlasProductPage;
